// @ts-check

import React, { ReactElement, ChangeEvent, useState, useEffect } from 'react'
import { Control, FieldErrors, UseFormRegister, UseFormWatch } from 'react-hook-form'
import { UseFormClearErrors } from 'react-hook-form/dist/types/form'

import { getCitiesByPostalCode, getStreetsByPostalCodeAndCityName } from '../../../api/senecApiUtils'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { setAltAddressFieldsSlice } from '../../../store/slices/alternativeAddressFieldsSlice'
import { FormInput, FormSelectSearchable } from '../index'

const Component = (props: {
  sliceName: string
  prefix?: string
  suffix?: string
  twoRows?: boolean
  errors?: FieldErrors
  register: UseFormRegister<any>
  watch: UseFormWatch<any>
  control: Control<any>
  clearErrors: UseFormClearErrors<any>
  setValue: (name: any, value: any) => void
  cityOptionsInitial: Array<{ label: string, value: number}>
  streetOptionsInitial: Array<{ label: string, value: number }>
  initialPostalCode?: string
}): ReactElement => {
  const {
    sliceName,
    prefix,
    suffix,
    twoRows,
    errors,
    register,
    watch,
    control,
    clearErrors,
    setValue,
    cityOptionsInitial,
    streetOptionsInitial
  } = props

  const postleitzahlId = `${prefix ? `${prefix}-` : ''}postleitzahl${suffix ? `-${suffix}` : ''}`
  const ortId = `${prefix ? `${prefix}-` : ''}ort${suffix ? `-${suffix}` : ''}`
  const strasseId = `${prefix ? `${prefix}-` : ''}strasse${suffix ? `-${suffix}` : ''}`
  const hausnummerId = `${prefix ? `${prefix}-` : ''}hausnummer${suffix ? `-${suffix}` : ''}`

  const showAlternativeFields = useAppSelector(state => state.alternativeAddressFields)
  const blackPostalCodes = useAppSelector(state => state.blackpostalcodes)
  const dispatch = useAppDispatch()

  const [postcodeValid, setPostcodeValid] = useState<boolean>(false)
  const [cityOptions, setCityOptions] = useState<Array<{ label: string, value: number }>>(cityOptionsInitial)
  const [streetOptions, setStreetOptions] = useState<Array<{ label: string, value: number}>>(streetOptionsInitial)

  const watchPostalCode = watch(postleitzahlId)
  const watchCity = watch(ortId)
  const watchStreet = watch(strasseId)

  useEffect(() => {
    const postalCode = props.initialPostalCode || watchPostalCode
    if (postalCode && validatePostalCode(postalCode)) {
      getCitiesByPostalCode(watchPostalCode)
        .then(data => {
          if (data && data.length > 0) {
            const citiesOptionsWithPostalCode: Array<{ label: string, value: number, postalCode: string }> = []
            data.map(city => {
              citiesOptionsWithPostalCode.push({
                label: city.name,
                value: city.id,
                postalCode: watchPostalCode
              })
              return false
            })
            setCityOptions(citiesOptionsWithPostalCode)
            const updateStateVal: {[key: string]: boolean} = { ...showAlternativeFields.values }
            updateStateVal[`${sliceName}City`] = false
            updateStateVal[`${sliceName}Street`] = false
            dispatch(setAltAddressFieldsSlice(updateStateVal))
          } else {
            const updateStateVal: {[key: string]: boolean} = { ...showAlternativeFields.values }
            updateStateVal[`${sliceName}City`] = true
            updateStateVal[`${sliceName}Street`] = true
            dispatch(setAltAddressFieldsSlice(updateStateVal))
          }
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.log(`error: ${error}`)
          const updateStateVal: {[key: string]: boolean} = { ...showAlternativeFields.values }
          updateStateVal[`${sliceName}City`] = true
          updateStateVal[`${sliceName}Street`] = true
          dispatch(setAltAddressFieldsSlice(updateStateVal))
        })
    }
  }, [watchPostalCode])

  const validatePostalCode = (postalCode: string) => {
    if (!blackPostalCodes.value.includes(postalCode)) {
      clearErrors(postleitzahlId)
      return true
    } else {
      return false
    }
  }

  const cityOnChangeFn = (newValue: { label: string, value: number}) => {
    const actualPostalCode = watchPostalCode

    getStreetsByPostalCodeAndCityName(actualPostalCode, newValue.label)
      .then(data => {
        if (data && data.length > 0) {
          const streetsOptions: Array<{ label: string, value: number }> = data.map((street, index) => ({
            label: street,
            value: index
          }))

          setStreetOptions(streetsOptions)

          const updateStateVal: {[key: string]: boolean} = { ...showAlternativeFields.values }
          updateStateVal[`${sliceName}Street`] = false
          dispatch(setAltAddressFieldsSlice(updateStateVal))
        } else {
          // In case we receive no data mocked data is shown.
          // eslint-disable-next-line no-console
          const updateStateVal: {[key: string]: boolean} = { ...showAlternativeFields.values }
          updateStateVal[`${sliceName}Street`] = true
          dispatch(setAltAddressFieldsSlice(updateStateVal))
        }
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error(`Error fetching streets: ${error}`)
        const updateStateVal: {[key: string]: boolean} = { ...showAlternativeFields.values }
        updateStateVal[`${sliceName}Street`] = true
        dispatch(setAltAddressFieldsSlice(updateStateVal))
      })
  }

  return (
    <div className="flex flex-wrap -mx-4">
      <div className={`w-full ${twoRows ? '' : 'lg:w-1/2'}`}>
        <div className="flex flex-wrap">
          <FormInput
            id={postleitzahlId}
            placeholder="Postleitzahl"
            label="Postleitzahl"
            className="w-full sm:w-1/2 px-4"
            errors={errors}
            register={register}
            disabled={!!(props.initialPostalCode)}
            validations={{
              required: 'Bitte füllen Sie dieses Feld aus.',
              pattern: {
                value: /^(?!01000|99999)(0[1-9]\d{3}|[1-9]\d{4})$/,
                message: 'Bitte geben Sie eine fünfstellige Postleitzahl ein.'
              },
              validate: (postalCode: string) => validatePostalCode(postalCode) || 'Leider können wir die SENEC.Cloud in deiner Region nicht anbieten.'
            }}
            onChangeFn={() => {
              if (errors && !errors[postleitzahlId]) {
                setPostcodeValid(true)
              } else {
                setPostcodeValid(false)
              }
            }}
          />
          {(!showAlternativeFields.values[`${sliceName}City`] &&
            watchPostalCode &&
            watchPostalCode.length > 0 &&
            cityOptions.length > 0 &&
            (errors && !errors[postleitzahlId])
          ) && (
            <FormSelectSearchable
              id={ortId}
              placeholder="Auswählen..."
              label="Ort"
              className="w-full sm:w-1/2 px-4"
              errors={errors}
              register={register}
              validations={{
                required: 'Bitte füllen Sie dieses Feld aus.'
              }}
              control={control}
              options={cityOptions}
              isSearchable={true}
              onChangeFn={cityOnChangeFn}
            />
          )}
          {showAlternativeFields.values[`${sliceName}City`] && (
            <FormInput
              id={`${ortId}-alt`}
              placeholder="Ort"
              label="Ort"
              className="w-full sm:w-1/2 px-4"
              errors={errors}
              register={register}
              validations={{
                required: 'Bitte füllen Sie dieses Feld aus.'
              }}
              onChangeFn={(e: ChangeEvent<HTMLInputElement>) => { setValue(ortId, { label: e.target.value, value: 1 }) }}
            />

          )}
        </div>
      </div>
      <div className={`w-full ${twoRows ? '' : 'lg:w-1/2'}`}>
        <div className="flex flex-wrap">
          {(!showAlternativeFields.values[`${sliceName}Street`] &&
            watchCity && streetOptions.length > 0 &&
            (errors && !errors[postleitzahlId])
          ) && (
            <FormSelectSearchable
              id={strasseId}
              placeholder="Straße"
              label="Straße"
              className="w-full sm:w-7/12 px-4"
              errors={errors}
              register={register}
              validations={{
                required: 'Bitte füllen Sie dieses Feld aus.'
              }}
              control={control}
              options={streetOptions}
              isSearchable={true}
            />
          )}
          {showAlternativeFields.values[`${sliceName}Street`] && (
            <FormInput
              id={`${strasseId}-alt`}
              placeholder="Straße"
              label="Straße"
              className="w-full sm:w-7/12 px-4"
              errors={errors}
              register={register}
              validations={{
                required: 'Bitte füllen Sie dieses Feld aus.'
              }}
              onChangeFn={(e: ChangeEvent<HTMLInputElement>) => { setValue(strasseId, { label: e.target.value, value: 1 }) }}
            />

          )}
          {((watchStreet &&
            (errors && !errors[postleitzahlId])) ||
            showAlternativeFields.values[`${sliceName}Street`]
          ) && (
            <FormInput
              id={hausnummerId}
              placeholder="Hausnummer"
              label="Hausnummer"
              className="w-full sm:w-5/12 px-4"
              errors={errors}
              register={register}
              validations={{
                required: 'Bitte füllen Sie dieses Feld aus.'
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default Component
